import {useEffect, useCallback, useRef} from 'react';
import {
  ACTIVE_HIGHLIGHT_STYLE,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_TYPES,
} from '../constants';

const INCONSISTENCY_BACKGROUND =
  HIGHLIGHT_COLORS[HIGHLIGHT_TYPES.INCONSISTENCY].error.background;
const INCONSISTENCY_ACTIVE_BORDER = ACTIVE_HIGHLIGHT_STYLE.border;
const INCONSISTENCY_ACTIVE_BOX_SHADOW = ACTIVE_HIGHLIGHT_STYLE.boxShadow;

const applyActiveStyle = (node) => {
  node.style.border = INCONSISTENCY_ACTIVE_BORDER;
  node.style.boxShadow = INCONSISTENCY_ACTIVE_BOX_SHADOW;
};

const removeActiveStyle = (node) => {
  node.style.border = 'none';
  node.style.boxShadow = 'none';
};

const createInconsistencyNode = ({width, height, isTextSource = false}) => {
  const node = document.createElement('div');

  node.style.cssText = `
    width: ${width}px;
    height: ${height}px;
    background-color: ${!isTextSource ? INCONSISTENCY_BACKGROUND : 'inherit'};
    mix-blend-mode: multiply;
    border: none;
    box-shadow: none;
    box-sizing: border-box;
    pointer-events: none;
    transition: border 0.15s ease, box-shadow 0.15s ease;
    user-select: none;
  `;

  return node;
};

const normalizeBboxToPageCoords = (bbox, pageWidth, pageHeight) => {
  return {
    x: bbox.left * pageWidth,
    y: bbox.top * pageHeight,
    width: (bbox.right - bbox.left) * pageWidth,
    height: (bbox.bottom - bbox.top) * pageHeight,
  };
};

export const useInconsistencyOverlays = ({instance, NutrientViewer}) => {
  const overlayIdsRef = useRef(new Set());
  const inconsistencyDataRef = useRef(new Map());
  const activeInconsistencyIdRef = useRef(null);

  const getInconsistencyAtPoint = useCallback(
    (pageIndex, point) => {
      if (!instance) return null;

      for (const [, data] of inconsistencyDataRef.current.entries()) {
        if (data.pageIndex !== pageIndex) continue;

        const {item} = data;
        const {box} = item.cell_grounding ?? item.grounding ?? {};
        if (!box) continue;

        const pageInfo = instance.pageInfoForIndex(pageIndex);
        if (!pageInfo) continue;

        const coords = normalizeBboxToPageCoords(
          box,
          pageInfo.width,
          pageInfo.height
        );

        if (
          point.x >= coords.x &&
          point.x <= coords.x + coords.width &&
          point.y >= coords.y &&
          point.y <= coords.y + coords.height
        ) {
          return data.inconsistency;
        }
      }
      return null;
    },
    [instance]
  );

  const clearInconsistencyOverlays = useCallback(() => {
    if (!instance) return;

    overlayIdsRef.current.forEach((id) => {
      try {
        instance.removeCustomOverlayItem(id);
      } catch (err) {
        console.warn(
          `[useInconsistencyOverlays] Could not remove overlay ${id}:`,
          err.message
        );
      }
    });

    overlayIdsRef.current.clear();
    inconsistencyDataRef.current.clear();
    activeInconsistencyIdRef.current = null;
  }, [instance]);

  const showInconsistencyOverlays = useCallback(
    (inconsistencies, activeInconsistencyId) => {
      if (!instance || !NutrientViewer || !inconsistencies?.length) {
        return;
      }

      clearInconsistencyOverlays();

      inconsistencies.forEach((inconsistency) => {
        // Each inconsistency may have multiple items (locations)
        const items = inconsistency.items || [inconsistency];

        items.forEach((item, itemIndex) => {
          const {page: pageIndex, box} =
            item.cell_grounding ?? item.grounding ?? {};

          if (!box || pageIndex == null) {
            console.warn(
              '[useInconsistencyOverlays] Item missing bbox or page:',
              inconsistency.id,
              itemIndex
            );
            return;
          }

          const pageInfo = instance.pageInfoForIndex(pageIndex);
          if (!pageInfo) {
            console.warn(
              '[useInconsistencyOverlays] Could not get page info for page:',
              pageIndex
            );
            return;
          }

          const coords = normalizeBboxToPageCoords(
            box,
            pageInfo.width,
            pageInfo.height
          );
          const isActive = inconsistency.id === activeInconsistencyId;

          const node = createInconsistencyNode({
            width: coords.width,
            height: coords.height,
            isTextSource: item.source === 'text',
          });

          if (isActive) {
            applyActiveStyle(node);
          }

          const overlayId = `inconsistency-${inconsistency.id}-${itemIndex}`;

          const overlayItem = new NutrientViewer.CustomOverlayItem({
            id: overlayId,
            node,
            pageIndex,
            position: new NutrientViewer.Geometry.Point({
              x: coords.x,
              y: coords.y,
            }),
            onAppear: () => {
              const grandparent = node.parentElement?.parentElement;
              if (grandparent) {
                grandparent.style.pointerEvents = 'none';
              }
            },
          });

          instance.setCustomOverlayItem(overlayItem);

          overlayIdsRef.current.add(overlayId);
          inconsistencyDataRef.current.set(`${inconsistency.id}-${itemIndex}`, {
            overlayId,
            pageIndex,
            inconsistency,
            item,
            node,
          });
        });
      });

      activeInconsistencyIdRef.current = activeInconsistencyId;
      console.log(
        `[useInconsistencyOverlays] Created ${overlayIdsRef.current.size} overlays`
      );
    },
    [instance, NutrientViewer, clearInconsistencyOverlays]
  );

  const setActiveInconsistency = useCallback((inconsistencyId) => {
    const previousActiveId = activeInconsistencyIdRef.current;
    if (previousActiveId === inconsistencyId) return;

    // Remove active style from all previous active nodes
    if (previousActiveId) {
      inconsistencyDataRef.current.forEach((data, key) => {
        if (key.startsWith(`${previousActiveId}-`) && data.node) {
          removeActiveStyle(data.node);
        }
      });
    }

    // Apply active style to all nodes of the new active inconsistency
    if (inconsistencyId) {
      inconsistencyDataRef.current.forEach((data, key) => {
        if (key.startsWith(`${inconsistencyId}-`) && data.node) {
          applyActiveStyle(data.node);
        }
      });
    }

    activeInconsistencyIdRef.current = inconsistencyId;
  }, []);

  const scrollToInconsistency = useCallback(
    (inconsistency) => {
      if (!instance || !NutrientViewer || !inconsistency) return;

      // Get the first item's location for scrolling
      const items = inconsistency.items || [inconsistency];
      const firstItem = items[0];
      const {page: pageIndex, box} =
        firstItem?.cell_grounding ?? firstItem?.grounding ?? {};

      if (pageIndex == null) return;

      if (!box) {
        instance.setViewState((viewState) =>
          viewState.set('currentPageIndex', pageIndex)
        );
        return;
      }

      const pageInfo = instance.pageInfoForIndex(pageIndex);
      if (!pageInfo) return;

      const coords = normalizeBboxToPageCoords(
        box,
        pageInfo.width,
        pageInfo.height
      );

      const rect = new NutrientViewer.Geometry.Rect({
        left: coords.x,
        top: coords.y,
        width: coords.width,
        height: coords.height,
      });

      instance.jumpToRect(pageIndex, rect);
    },
    [instance, NutrientViewer]
  );

  useEffect(() => {
    if (!instance) return;

    const handlePagePress = (event) => {
      const {pageIndex, point} = event;
      const inconsistency = getInconsistencyAtPoint(pageIndex, point);
      if (inconsistency) {
        scrollToInconsistency(inconsistency);
        setActiveInconsistency(inconsistency.id);
      }
    };

    instance.addEventListener('page.press', handlePagePress);

    return () => {
      instance.removeEventListener('page.press', handlePagePress);
    };
  }, [
    instance,
    getInconsistencyAtPoint,
    scrollToInconsistency,
    setActiveInconsistency,
  ]);

  return {
    showInconsistencyOverlays,
    setActiveInconsistency,
    clearInconsistencyOverlays,
    scrollToInconsistency,
  };
};

export default useInconsistencyOverlays;
